import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, HelpCircle, Loader2, PhoneCall, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PhotoPicker } from "@/components/PhotoPicker";
import { api, errMsg } from "@/lib/api";

const FIT = {
  encaja: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Puede ayudarle", CheckCircle2],
  quizas: ["bg-amber-50 text-amber-800 border-amber-200", "Quizás le ayude", HelpCircle],
  no_encaja: ["bg-slate-50 text-slate-700 border-slate-200", "No parece necesario", XCircle],
};

const Result = ({ r, slug }) => {
  const [cls, label, Icon] = FIT[r.fit];
  return (
    <div className="space-y-3 text-sm" data-testid="fit-result">
      {r.urgency === "urgente" && <p className="bg-[#DC2626] text-white rounded p-3 font-bold flex gap-2" data-testid="fit-urgent"><PhoneCall size={18} className="shrink-0" /> Vemos señales que conviene que revise un veterinario cuanto antes. Llámale antes de dar ningún producto.</p>}
      <div className={`border rounded p-3 flex items-center gap-2 font-display font-bold text-lg ${cls}`} data-testid={`fit-verdict-${r.fit}`}><Icon size={22} /> {label}</div>
      {r.observed && <p><b className="text-[#0B1D3A]">Lo que vemos:</b> {r.observed}</p>}
      {r.reason && <p data-testid="fit-reason"><b className="text-[#0B1D3A]">Por qué:</b> {r.reason}</p>}
      {r.tip && <p><b className="text-[#0B1D3A]">Consejo:</b> {r.tip}</p>}
      {r.photo_request && <p className="bg-[#FEF3C7] text-[#92400E] rounded p-3" data-testid="fit-photo-request">{r.photo_request}</p>}
      <p className="text-xs text-slate-500">{r.vet_always}</p>
      <Link to={`/diagnostico?producto=${slug}`} className="inline-block text-[#2563EB] font-bold underline underline-offset-4" data-testid="fit-full-diagnosis">Hacer el diagnóstico completo</Link>
    </div>
  );
};

export const ProductFit = ({ p }) => {
  const [photo, setPhoto] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const run = async () => {
    setBusy(true); setR(null);
    try { setR((await api.post("/diagnosis/product-fit", { slug: p.slug, photo, note })).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white font-display font-bold rounded-full py-3 transition-colors" data-testid="product-fit-open">
          <Sparkles size={18} /> ¿Le conviene a mi caballo? Analízalo con una foto
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-fit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B1D3A]">¿{p.name.replace("FAUNNY ", "")} le conviene?</DialogTitle></DialogHeader>
        <PhotoPicker required photo={photo} setPhoto={(v) => { setPhoto(v); setR(null); }} />
        <Textarea rows={2} maxLength={600} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional: edad, uso o qué te preocupa" data-testid="product-fit-note" />
        <button type="button" disabled={!photo || busy} onClick={run} data-testid="product-fit-submit"
          className="w-full flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-300 text-white font-display font-bold rounded-full py-3 transition-colors">
          {busy ? <><Loader2 size={18} className="animate-spin" /> Analizando la foto…</> : "Analizar"}
        </button>
        {r && <Result r={r} slug={p.slug} />}
      </DialogContent>
    </Dialog>
  );
};
